import { Component, ComponentRef, HostListener, OnInit, ViewChild, ViewContainerRef } from '@angular/core'
import { environment } from 'src/environments/environment'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { start_login, start_login_with_otp, start_sso_login } from 'src/app/store/action/auth.action'
import { CommonService } from 'src/app/services/common/common.service'
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component'
import { Subject } from 'rxjs'
import { AuthService } from 'src/app/services/auth/auth.service'
import { ActivatedRoute } from '@angular/router'
import { LoginDiscoveryResponseApiModel } from 'src/app/models/api.model'
import { AuthType } from 'src/app/models/model'
import { DynamicMsalService } from 'src/app/services/common/dynamic.msal.service'
import { IPublicClientApplication } from '@azure/msal-browser'
import { LocalStorageService } from 'src/app/services/common/local-storage.service'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  companyDomain!: string
  companyLogoURL!: string

  passwordBasedLoginForm!: FormGroup
  otpBasedLoginForm!: FormGroup

  showPasswordLogin = false
  showOtpLogin = false
  showOtp = false
  ssoLogin = false

  otpLength = 4
  resendCountdown = 30
  resendAttempts = 0
  maxResendAttempts = 3
  canResendOtp = false
  resendLimitReached = false
  resendTimerRef: any

  private readonly PASSWORD = 'password'
  private readonly _destroying$ = new Subject<void>()

  companySettings!: LoginDiscoveryResponseApiModel

  @ViewChild('targetContainer', { read: ViewContainerRef })
  targetContainer!: ViewContainerRef

  targetComponentRef!: ComponentRef<ForgotPasswordComponent>
  private msalInstance: IPublicClientApplication
  private ssoInProgress = false

  otpConfig = {
    length: this.otpLength,
    allowNumbersOnly: true,
    inputClass: 'otp-input',
    containerClass: 'otp-input-container',
    isPasswordInput: false,
    autofocus: true,
    disableAutoFocus: false
  }

  constructor(
    private formBuilder: FormBuilder,
    private appState: Store<AppState>,
    private cs: CommonService,
    private readonly loginService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly dynamicMsalService: DynamicMsalService
  ) {}

  @HostListener('document:keydown.enter')
  onEnter() {
    if (this.isPasswordEnabled()) this.login()
    if (this.isOTPEnabled() && this.showOtp) this.loginWithOtp()
  }

  ngOnInit(): void {
    this.companyDomain = this.route.snapshot.paramMap.get('companyDomain')!
    if (!this.companyDomain) {
      this.cs.showError('Company domain missing')
      this.cs.routeTo('/')
      return
    }
    LocalStorageService.set('companyDomain', this.companyDomain)
    this.setCompanyLogoURL()
    this.discoverLoginOptions()
  }

  private discoverLoginOptions() {
    if (this.companyDomain.toUpperCase() === 'RMS') {
      this.companySettings = {
        authType: AuthType.PASSWORD,
        clientId: '',
        tenantId: '',
        ssoProvider: '',
        companyGroupId: '',
        companyGroupCode: '',
        companyDomain: this.companyDomain
      }
      this.buildPasswordBasedLoginForm()
    } else {
      this.loginService
        .discoverLoginOptions(this.companyDomain)
        .subscribe((response: LoginDiscoveryResponseApiModel) => {
          if (!response) return
          this.companySettings = response
          this.showPasswordLogin = response.authType === AuthType.PASSWORD
          this.showOtpLogin = response.authType === AuthType.OTP
          this.ssoLogin = response.authType === AuthType.SSO

          if (response.authType === AuthType.PASSWORD) {
            this.buildPasswordBasedLoginForm()
          } else if (response.authType === AuthType.OTP) {
            this.buildOtpBasedLoginForm()
          } else if (response.authType === AuthType.SSO) {
            const authority = `https://login.microsoftonline.com/${this.companySettings.tenantId}`
            this.dynamicMsalService.init(response.clientId, authority).catch((error: any) => {
              console.error('MSAL pre-initialization error:', error)
            })
          }
        })
    }
  }

  isPasswordEnabled(): boolean {
    return this.companySettings?.authType === AuthType.PASSWORD
  }

  isOTPEnabled(): boolean {
    return this.companySettings?.authType === AuthType.OTP
  }

  isSSOEnabled(): boolean {
    return this.companySettings?.authType === AuthType.SSO
  }

  setCompanyLogoURL() {
    this.companyLogoURL = environment.gcp_storage_endpoint + 'company-logos/' + this.companyDomain + '.jpg'
  }

  buildPasswordBasedLoginForm() {
    this.passwordBasedLoginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      [this.PASSWORD]: ['', [Validators.required]]
    })
  }

  buildOtpBasedLoginForm() {
    this.otpBasedLoginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      otp: ['', [Validators.required]]
    })
  }

  login() {
    if (this.passwordBasedLoginForm.invalid) {
      this.cs.showError('Enter valid email and password')
      return
    }
    this.appState.dispatch(start_login(this.passwordBasedLoginForm.value))
  }

  generateOtp() {
    if (this.otpBasedLoginForm.get('email')?.invalid) {
      this.cs.showError('Enter valid email')
      return
    }

    this.loginService.sendLoginOTP(this.otpBasedLoginForm.value.email).subscribe(() => {
      this.showOtp = true
      this.startResendTimer()
      this.cs.showSuccess('If an account exists for this email, an OTP will be sent.')
    })
  }

  onOtpChange(otp: string) {
    this.otpBasedLoginForm.get('otp')?.setValue(otp)
  }

  loginWithOtp() {
    const { email, otp } = this.otpBasedLoginForm.value
    if (!otp || otp.length !== this.otpLength) return
    this.appState.dispatch(start_login_with_otp({ email, otp }))
  }

  private clearMsalInteractionState(): void {
    const sessionKeys = Object.keys(sessionStorage)
    for (const key of sessionKeys) {
      if (key.toLowerCase().includes('msal') || key.toLowerCase().includes('interaction.status')) {
        sessionStorage.removeItem(key)
      }
    }
    const localKeys = Object.keys(localStorage)
    for (const key of localKeys) {
      if (key.toLowerCase().includes('msal') || key.toLowerCase().includes('interaction.status')) {
        localStorage.removeItem(key)
      }
    }
  }

  loginWithSSO() {
    if (this.ssoInProgress) return
    if (!this.companySettings) {
      this.cs.showError('SSO not configured')
      return
    }

    this.ssoInProgress = true

    try {
      if (this.companySettings.ssoProvider === 'MICROSOFT') {
        const authority = `https://login.microsoftonline.com/${this.companySettings.tenantId}`
        const msalInstancePromise = this.msalInstance
          ? Promise.resolve(this.msalInstance)
          : this.dynamicMsalService.init(this.companySettings.clientId, authority).then(instance => {
              this.msalInstance = instance
              return instance
            })

        //this.clearMsalInteractionState()

        msalInstancePromise
          .then(instance =>
            instance.loginPopup({
              scopes: ['openid', 'profile', 'email']
            })
          )
          .then((result: any) => {
            if (result?.idToken) {
              this.sendTokenToBE(result.idToken)
            }
          })
          .catch((error: any) => {
            console.error('MSAL login error:', error)
            return null
          })
      }
    } catch (err) {
      console.error('SSO login error:', err)
      this.cs.showError('SSO login failed')
    } finally {
      this.ssoInProgress = false
    }
  }

  sendTokenToBE(id_token: string) {
    this.appState.dispatch(start_sso_login({ id_token, companyDomain: this.companyDomain }))
  }

  startResendTimer() {
    this.canResendOtp = false
    this.resendCountdown = 30

    this.resendTimerRef = setInterval(() => {
      this.resendCountdown--
      if (this.resendCountdown === 0) {
        this.canResendOtp = true
        clearInterval(this.resendTimerRef)
      }
    }, 1000)
  }

  openTargetComponent() {
    if (!this.targetComponentRef) {
      this.targetComponentRef = this.targetContainer.createComponent(ForgotPasswordComponent)
    } else {
      this.targetComponentRef.instance.ngOnInit()
    }
  }

  handlePasswordChange($event: any) {
    this.passwordBasedLoginForm.controls[this.PASSWORD].setValue($event)
  }

  resendOtp(): void {
    if (this.resendLimitReached || !this.canResendOtp) {
      return
    }

    this.resendAttempts++

    if (this.resendAttempts >= this.maxResendAttempts) {
      this.resendLimitReached = true
      this.canResendOtp = false
      return
    }

    this.generateOtp()
  }

  ngOnDestroy(): void {
    this._destroying$.next()
    this._destroying$.complete()
    clearInterval(this.resendTimerRef)
  }
}
